import { safeLocalStorage } from '@/lib/safeStorage';
import { useCallback, useEffect, useState } from 'react';

const MESSAGE_DRAFT_KEY_PREFIX = 'ruta-message-draft-';

const getStorageKey = (customerId: string) => `${MESSAGE_DRAFT_KEY_PREFIX}${customerId}`;

export function getMessageDraft(customerId: string): string {
  if (!customerId) return '';
  try {
    return safeLocalStorage.getItem(getStorageKey(customerId)) ?? '';
  } catch {
    return '';
  }
}

export function setMessageDraft(customerId: string, value: string): void {
  if (!customerId) return;
  try {
    const key = getStorageKey(customerId);
    if (value) {
      safeLocalStorage.setItem(key, value);
    } else {
      safeLocalStorage.removeItem(key);
    }
  } catch (error) {
    console.warn('Failed to write message draft to localStorage:', error);
  }
}

/**
 * Persist an in-progress customer message to localStorage so it "bounces back"
 * when the user reopens the conversation. Keyed per customer.
 */
export function useMessageDraft(customerId: string) {
  const [draft, setDraftState] = useState('');

  useEffect(() => {
    setDraftState(getMessageDraft(customerId));
  }, [customerId]);

  const setDraft = useCallback(
    (value: string | ((prev: string) => string)) => {
      setDraftState((prev) => {
        const next = typeof value === 'function' ? value(prev) : value;
        setMessageDraft(customerId, next);
        return next;
      });
    },
    [customerId],
  );

  return [draft, setDraft] as const;
}
